import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SpeedIcon from '@mui/icons-material/Speed';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import { plantsApi } from '../../services/api';
import { formatNuf } from '../../utils/formatNuf';

// Formattazione numeri
const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '-';
  return parseFloat(num).toLocaleString('it-IT', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Formattazione valuta
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Formattazione data
const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

// Nome mese italiano
const getMonthName = (month, year) => {
  return new Date(year, month - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
};

// Colore saldo
const getBalanceColor = (balance) => {
  if (balance < 0) return 'error.main';
  if (balance === 0) return 'text.secondary';
  return 'success.main';
};

// Icona tipo evento
const getEventIcon = (type, hasAnomaly) => {
  switch (type) {
    case 'initial':
      return <PlayArrowIcon fontSize="small" />;
    case 'delivery':
      return <LocalShippingIcon fontSize="small" />;
    case 'consumption':
      return <ReceiptIcon fontSize="small" />;
    case 'reading':
      return <SpeedIcon fontSize="small" color={hasAnomaly ? 'warning' : 'primary'} />;
    default:
      return <PropaneTankIcon fontSize="small" />;
  }
};

// Componente riga singola evento
const EventRow = ({ event, capacita }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);
  const hasDetails = event.details && Object.keys(event.details).length > 0;
  
  const getRowStyle = () => {
    switch (event.type) {
      case 'initial':
        return { bgcolor: '#e3f2fd' };
      case 'delivery':
        return { bgcolor: '#e8f5e9' };
      case 'consumption':
        return { bgcolor: '#fff8f8' };
      case 'reading':
        return { bgcolor: event.details?.has_anomaly ? '#fff3e0' : '#f3f0ff' };
      default:
        return {};
    }
  };

  const getLabelChip = () => {
    switch (event.type) {
      case 'initial':
        return <Chip label="Inizio" size="small" color="info" variant="outlined" />;
      case 'delivery':
        return <Chip label="Rifornimento" size="small" color="success" variant="outlined" />;
      case 'consumption':
        return <Chip label="Consumo" size="small" color="error" variant="outlined" />;
      case 'reading':
        if (event.isStartingPoint) {
          return <Chip label="Partenza" size="small" color="info" />;
        }
        return <Chip label="Checkpoint" size="small" color={event.details?.has_anomaly ? 'warning' : 'primary'} />;
      default:
        return <Chip label="Evento" size="small" variant="outlined" />;
    }
  };

  return (
    <>
      <TableRow 
        sx={{ 
          ...getRowStyle(),
          cursor: hasDetails ? 'pointer' : 'default',
          '&:hover': { opacity: 0.9 }
        }}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        {/* Colonna Data */}
        <TableCell sx={{ width: isMobile ? 70 : 100, py: 1 }}>
          <Typography variant={isMobile ? 'caption' : 'body2'}>{formatDate(event.date)}</Typography>
        </TableCell>
        
        {/* Colonna Tipo - nascosta su mobile */}
        {!isMobile && (
          <TableCell sx={{ width: 150, py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getEventIcon(event.type, event.details?.has_anomaly)}
              {getLabelChip()}
            </Box>
          </TableCell>
        )}
        
        {/* Colonna Descrizione */}
        <TableCell sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isMobile && getEventIcon(event.type, event.details?.has_anomaly)}
            <Typography variant={isMobile ? 'caption' : 'body2'} noWrap sx={{ maxWidth: isMobile ? 120 : 300 }}>
              {event.description}
            </Typography>
          </Box>
        </TableCell>
        
        {/* Colonna Rilevato - nascosta su mobile */}
        <TableCell align="right" sx={{ width: isMobile ? 0 : 120, py: 1, display: { xs: 'none', sm: 'table-cell' } }}>
          {event.type === 'reading' && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body1" fontWeight="bold" color="primary.main">
                {formatNumber(event.actualBalance || event.liters)} L
              </Typography>
              {!event.isStartingPoint && event.discrepancy !== null && event.discrepancy !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Typography 
                    variant="caption" 
                    fontWeight="bold"
                    color={Math.abs(event.discrepancy) > 50 ? 'warning.main' : 'success.main'}
                  >
                    {event.discrepancy > 0 ? '+' : ''}{formatNumber(event.discrepancy)} L
                  </Typography>
                  {Math.abs(event.discrepancy) > 50 && (
                    <WarningIcon sx={{ fontSize: 12 }} color="warning" />
                  )}
                </Box>
              )}
              {event.isStartingPoint && (
                <Typography variant="caption" color="info.main">
                  Partenza
                </Typography>
              )}
            </Box>
          )}
        </TableCell>

        {/* Colonna +/- Litri */}
        <TableCell align="right" sx={{ width: isMobile ? 75 : 100, py: 1 }}>
          {event.change > 0 && (
            <Typography variant="body1" fontWeight="bold" color="success.main">
              +{formatNumber(event.liters)} L
            </Typography>
          )}
          {event.change < 0 && (
            <Typography variant="body1" fontWeight="bold" color="error.main">
              -{formatNumber(Math.abs(event.liters))} L
            </Typography>
          )}
          {event.type === 'initial' && (
            <Typography variant="body1" fontWeight="bold" color="info.main">
              {formatNumber(event.liters)} L
            </Typography>
          )}
        </TableCell>
        
        {/* Colonna Saldo Litri */}
        <TableCell 
          align="right" 
          sx={{ 
            width: isMobile ? 90 : 140, 
            py: 1,
            borderLeft: '3px solid',
            borderColor: event.runningBalance < 0 ? 'error.main' : 'primary.main',
            bgcolor: event.runningBalance < 0 ? 'error.50' : 'grey.50'
          }}
        >
          <Typography 
            variant="body1" 
            fontWeight="bold" 
            color={getBalanceColor(event.runningBalance)}
            sx={{ fontSize: '1rem' }}
          >
            {formatNumber(event.runningBalance)} L
          </Typography>
          {capacita > 0 && (
            <Typography variant="caption" color="text.secondary">
              {formatNumber((event.runningBalance / capacita) * 100, 1)}%
            </Typography>
          )}
        </TableCell>
        
        {/* Colonna Expand */}
        <TableCell sx={{ width: 40, py: 1, display: { xs: 'none', sm: 'table-cell' } }}>
          {hasDetails && (
            <IconButton size="small">
              <ExpandMoreIcon 
                sx={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }} 
              />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      
      {hasDetails && expanded && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'grey.50' }}>
            <Collapse in={expanded}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {event.type === 'delivery' && (
                    <>
                      {event.details.ddt_number && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">DDT</Typography>
                          <Typography variant="body2" fontWeight="medium">{event.details.ddt_number}</Typography>
                        </Grid>
                      )}
                      {event.details.invoice_number && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Fattura</Typography>
                          <Typography variant="body2" fontWeight="medium">{event.details.invoice_number}</Typography>
                        </Grid>
                      )}
                      {event.details.price_per_liter && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Prezzo/L</Typography>
                          <Typography variant="body2" fontWeight="medium">{formatCurrency(event.details.price_per_liter)}</Typography>
                        </Grid>
                      )}
                      {event.details.total_amount && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Totale</Typography>
                          <Typography variant="body2" fontWeight="medium">{formatCurrency(event.details.total_amount)}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  
                  {event.type === 'consumption' && (
                    <>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">N Fattura</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatNuf(event.details.sezionale, event.details.nuf)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Contatore</Typography>
                        <Typography variant="body2" fontWeight="medium">{event.details.f_nu_conta || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Importo</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatCurrency(event.details.f_totale)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Pagato</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatCurrency(event.details.f_paga_imp)}</Typography>
                      </Grid>
                    </>
                  )}
                  
                  {event.type === 'reading' && (
                    <>
                      {event.details.technician_name && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Tecnico</Typography>
                          <Typography variant="body2" fontWeight="medium">{event.details.technician_name}</Typography>
                        </Grid>
                      )}
                      {event.calculatedBalance !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Calcolato</Typography>
                          <Typography variant="body2" fontWeight="medium">{formatNumber(event.calculatedBalance)} L</Typography>
                        </Grid>
                      )}
                      {event.discrepancy !== undefined && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Scostamento</Typography>
                          <Typography 
                            variant="body2" 
                            fontWeight="medium"
                            color={event.details?.has_anomaly ? 'warning.main' : 'text.primary'}
                          >
                            {event.discrepancy > 0 ? '+' : ''}{formatNumber(event.discrepancy)} L
                          </Typography>
                        </Grid>
                      )}
                      {event.details.note && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Note</Typography>
                          <Typography variant="body2">{event.details.note}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Componente gruppo mese per consumi
const MonthlyConsumptionGroup = ({ month, year, events, capacita }) => {
  const [expanded, setExpanded] = useState(false);
  
  const totals = useMemo(() => {
    let totalLiters = 0;
    let totalAmount = 0;
    events.forEach(e => {
      totalLiters += e.liters || 0;
      totalAmount += e.details?.f_totale || 0;
    });
    return { totalLiters, totalAmount, count: events.length };
  }, [events]);

  const endBalance = events.length > 0 ? events[events.length - 1].runningBalance : 0;

  return (
    <Accordion 
      expanded={expanded} 
      onChange={() => setExpanded(!expanded)}
      sx={{ 
        mb: 1,
        '&:before': { display: 'none' },
        bgcolor: '#fff8f8',
        border: '1px solid',
        borderColor: 'error.light'
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 0 }}>
          {/* Simula struttura tabella: Data + Tipo + Descrizione */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <ReceiptIcon color="error" />
            <Typography variant="subtitle1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
              {getMonthName(month, year)}
            </Typography>
            <Chip label={`${totals.count} fatture`} size="small" color="error" variant="outlined" />
          </Box>
          
          {/* Colonna Rilevato - vuota per gruppi mensili */}
          <Box sx={{ width: 120, textAlign: 'right' }} />
          
          {/* Colonna +/- Litri (Consumo totale) */}
          <Box sx={{ width: 100, textAlign: 'right', mr: 2 }}>
            <Typography variant="body1" fontWeight="bold" color="error.main">
              -{formatNumber(totals.totalLiters)} L
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(totals.totalAmount)}
            </Typography>
          </Box>
          
          {/* Colonna Saldo */}
          <Box 
            sx={{ 
              textAlign: 'right', 
              width: 140,
              borderLeft: '3px solid', 
              borderColor: endBalance < 0 ? 'error.main' : 'primary.main',
              pl: 2,
              bgcolor: endBalance < 0 ? 'error.50' : 'grey.50',
              py: 0.5,
              pr: 1,
              mr: 5
            }}
          >
            <Typography variant="body1" fontWeight="bold" color={getBalanceColor(endBalance)} sx={{ fontSize: '1rem' }}>
              {formatNumber(endBalance)} L
            </Typography>
            {capacita > 0 && (
              <Typography variant="caption" color="text.secondary">
                {formatNumber((endBalance / capacita) * 100, 1)}%
              </Typography>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, bgcolor: 'white' }}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {[...events].reverse().map((event, idx) => (
                <EventRow key={`${event.type}-${event.dateTime}-${idx}`} event={event} capacita={capacita} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

// Componente principale
const LitersControlTimeline = ({ plantId }) => {
  const [startCheckpoint, setStartCheckpoint] = useState('initial'); // 'initial' o id del checkpoint
  const [endCheckpoint, setEndCheckpoint] = useState('latest'); // 'latest' o id del checkpoint
  
  // Query dati timeline - carica TUTTO dalla giacenza iniziale
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['plant-liters-control', plantId],
    queryFn: async () => {
      const response = await plantsApi.getLitersControl(plantId, { limit: 1000 });
      return response.data?.data || { events: [], stats: {}, checkpoints: [] };
    },
    enabled: !!plantId
  });

  // Estrai i checkpoint (giacenza iniziale + rilevazioni)
  const checkpoints = useMemo(() => {
    if (!data?.events) return [];
    const cps = [];
    
    // Aggiungi giacenza iniziale come primo checkpoint
    const initial = data.events.find(e => e.type === 'initial');
    if (initial) {
      cps.push({
        id: 'initial',
        label: `Giacenza iniziale - ${formatDate(initial.date)}`,
        date: initial.date,
        liters: initial.liters,
        event: initial
      });
    }
    
    // Aggiungi tutte le rilevazioni come checkpoint
    data.events
      .filter(e => e.type === 'reading')
      .forEach((reading, idx) => {
        cps.push({
          id: `reading-${reading.details?.id || idx}`,
          label: `Rilevazione ${formatDate(reading.date)} - ${formatNumber(reading.liters)} L`,
          date: reading.date,
          liters: reading.liters,
          event: reading
        });
      });
    
    return cps;
  }, [data?.events]);

  // Filtra gli eventi tra i due checkpoint selezionati
  const filteredData = useMemo(() => {
    if (!data?.events) return { events: [], stats: {} };
    
    let events = [...data.events];
    let startIdx = 0;
    let endIdx = events.length - 1;
    
    // Trova indice del checkpoint di inizio
    if (startCheckpoint === 'initial') {
      startIdx = events.findIndex(e => e.type === 'initial');
      if (startIdx === -1) startIdx = 0;
    } else {
      const checkpointId = startCheckpoint.replace('reading-', '');
      startIdx = events.findIndex(e => 
        e.type === 'reading' && (String(e.details?.id) === checkpointId || e.details?.id === parseInt(checkpointId))
      );
      if (startIdx === -1) startIdx = 0;
    }
    
    // Trova indice del checkpoint di fine
    if (endCheckpoint === 'latest') {
      endIdx = events.length - 1;
    } else {
      const checkpointId = endCheckpoint.replace('reading-', '');
      endIdx = events.findIndex(e => 
        e.type === 'reading' && (String(e.details?.id) === checkpointId || e.details?.id === parseInt(checkpointId))
      );
      if (endIdx === -1) endIdx = events.length - 1;
    }
    
    // Assicurati che startIdx <= endIdx
    if (startIdx > endIdx) {
      [startIdx, endIdx] = [endIdx, startIdx];
    }
    
    // Filtra eventi nel range
    const filteredEvents = events.slice(startIdx, endIdx + 1);
    
    // Ricalcola statistiche per il periodo filtrato
    const deliveries = filteredEvents.filter(e => e.type === 'delivery');
    const consumptions = filteredEvents.filter(e => e.type === 'consumption');
    const readings = filteredEvents.filter(e => e.type === 'reading');
    
    // Trova l'ultima rilevazione nel periodo
    const lastReading = [...readings].reverse().find(r => r.actualBalance !== undefined);
    
    const startEvent = filteredEvents[0];
    const endEvent = filteredEvents[filteredEvents.length - 1];
    
    const stats = {
      ...data.stats,
      periodStart: startEvent?.date,
      periodEnd: endEvent?.date,
      startBalance: startEvent?.runningBalance || 0,
      endBalance: endEvent?.runningBalance || 0,
      totalDeliveries: deliveries.length,
      totalDeliveryLiters: deliveries.reduce((sum, d) => sum + (d.liters || 0), 0),
      totalConsumptions: consumptions.length,
      totalConsumptionLiters: consumptions.reduce((sum, e) => sum + (e.liters || 0), 0),
      totalReadings: readings.length,
      readingsWithAnomalies: readings.filter(r => r.details?.has_anomaly).length,
      lastReadingValue: lastReading?.actualBalance,
      lastReadingDate: lastReading?.date,
    };
    
    return { events: filteredEvents, stats };
  }, [data, startCheckpoint, endCheckpoint]);

  // Raggruppa consumi per mese
  const groupedEvents = useMemo(() => {
    if (!filteredData?.events) return [];
    
    const result = [];
    let currentMonthGroup = null;
    
    filteredData.events.forEach((event) => {
      if (event.type === 'consumption') {
        const eventDate = new Date(event.date);
        const month = eventDate.getMonth() + 1;
        const year = eventDate.getFullYear();
        const monthKey = `${year}-${month}`;
        
        if (currentMonthGroup && currentMonthGroup.key === monthKey) {
          currentMonthGroup.events.push(event);
        } else {
          if (currentMonthGroup) {
            result.push(currentMonthGroup);
          }
          currentMonthGroup = {
            type: 'month-group',
            key: monthKey,
            month,
            year,
            events: [event]
          };
        }
      } else {
        if (currentMonthGroup) {
          result.push(currentMonthGroup);
          currentMonthGroup = null;
        }
        result.push({ type: 'single', event });
      }
    });
    
    if (currentMonthGroup) {
      result.push(currentMonthGroup);
    }
    
    // Inverti l'ordine per mostrare dal più recente al più vecchio
    return result.reverse();
  }, [filteredData?.events]);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Errore nel caricamento dei dati: {error.message}
      </Alert>
    );
  }

  const { events, stats } = filteredData;

  return (
    <Box>
      {/* Header con filtri checkpoint */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            <PropaneTankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Controllo Litri
          </Typography>
          <Tooltip title="Aggiorna">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filtro tra checkpoint */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <FilterAltIcon color="action" />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>Da:</Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <Select
              value={startCheckpoint}
              onChange={(e) => setStartCheckpoint(e.target.value)}
            >
              {checkpoints.map(cp => (
                <MenuItem key={cp.id} value={cp.id}>{cp.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 20 }}>A:</Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <Select
              value={endCheckpoint}
              onChange={(e) => setEndCheckpoint(e.target.value)}
            >
              <MenuItem value="latest">Oggi (ultimo movimento)</MenuItem>
              {checkpoints.slice(1).map(cp => (
                <MenuItem key={cp.id} value={cp.id}>{cp.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Card statistiche */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderColor: stats.endBalance < 0 ? 'error.main' : 'primary.main', borderWidth: 2 }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Saldo Calcolato
                    </Typography>
                    <Typography 
                      variant="h4" 
                      fontWeight="bold"
                      color={getBalanceColor(stats.endBalance)}
                    >
                      {formatNumber(stats.endBalance)} L
                    </Typography>
                    {stats.capacita > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber((stats.endBalance / stats.capacita) * 100, 1)}%
                      </Typography>
                    )}
                  </Box>
                  {stats.lastReadingValue !== undefined && (
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">
                        Rilevato
                      </Typography>
                      <Typography 
                        variant="h4" 
                        fontWeight="bold"
                        color="primary.main"
                      >
                        {formatNumber(stats.lastReadingValue)} L
                      </Typography>
                      {stats.capacita > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {formatNumber((stats.lastReadingValue / stats.capacita) * 100, 1)}%
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalShippingIcon color="success" fontSize="small" />
                  <Typography variant="caption" color="text.secondary">
                    Rifornimenti ({stats.totalDeliveries})
                  </Typography>
                </Box>
                <Typography variant="h5" color="success.main" fontWeight="bold">
                  +{formatNumber(stats.totalDeliveryLiters)} L
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ReceiptIcon color="error" fontSize="small" />
                  <Typography variant="caption" color="text.secondary">
                    Consumi ({stats.totalConsumptions})
                  </Typography>
                </Box>
                <Typography variant="h5" color="error.main" fontWeight="bold">
                  -{formatNumber(stats.totalConsumptionLiters)} L
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">
                  {stats.lastReadingValue !== undefined ? 'Scarto Rilevato vs Calcolato' : 'Differenza Periodo'}
                </Typography>
                {stats.lastReadingValue !== undefined ? (
                  <>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold"
                      color={(stats.lastReadingValue - stats.endBalance) >= 0 ? 'success.main' : 'error.main'}
                    >
                      {(stats.lastReadingValue - stats.endBalance) >= 0 ? '+' : ''}
                      {formatNumber(stats.lastReadingValue - stats.endBalance)} L
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(stats.lastReadingValue)} L vs {formatNumber(stats.endBalance)} L
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold"
                      color={(stats.endBalance - stats.startBalance) >= 0 ? 'success.main' : 'error.main'}
                    >
                      {(stats.endBalance - stats.startBalance) >= 0 ? '+' : ''}
                      {formatNumber(stats.endBalance - stats.startBalance)} L
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      da {formatNumber(stats.startBalance)} L
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista eventi */}
      {events.length === 0 ? (
        <Alert severity="info">
          Nessun evento trovato per questo impianto.
          {!stats.initialDate && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Suggerimento:</strong> Imposta una giacenza iniziale per iniziare il tracciamento.
            </Typography>
          )}
        </Alert>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary">
              {events.length} movimenti dal {formatDate(stats.periodStart)} al {formatDate(stats.periodEnd)}
            </Typography>
          </Box>
          
          <Box sx={{ p: 2 }}>
            {groupedEvents.map((item, index) => {
              if (item.type === 'month-group') {
                return (
                  <MonthlyConsumptionGroup
                    key={item.key}
                    month={item.month}
                    year={item.year}
                    events={item.events}
                    capacita={stats.capacita}
                  />
                );
              } else {
                return (
                  <Paper key={`${item.event.type}-${item.event.dateTime}-${index}`} sx={{ mb: 1 }} variant="outlined">
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <EventRow event={item.event} capacita={stats.capacita} />
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                );
              }
            })}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default LitersControlTimeline;
